import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api/client';
import StatusBadge from '../common/StatusBadge';
import { formatFileSize } from '../common/DurationDisplay';
import DurationDisplay from '../common/DurationDisplay';
import ConfirmDialog from '../common/ConfirmDialog';
import { format, parseISO } from 'date-fns';

const VIEW_MODES = {
  grid: 'grid',
  list: 'list',
};

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'ready', label: 'Listo' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'processing', label: 'Procesando' },
  { value: 'error', label: 'Error' },
  { value: 'uploading', label: 'Subiendo' },
];

const CATEGORY_OPTIONS = [
  { value: 'all', label: 'Todas' },
  { value: 'music', label: 'Música' },
  { value: 'program', label: 'Programa' },
  { value: 'advertising', label: 'Publicidad' },
  { value: 'jingle', label: 'Jingle' },
  { value: 'promo', label: 'Promo' },
  { value: 'fx', label: 'FX / Sweep' },
  { value: 'playlist', label: 'Playlist' },
  { value: 'special', label: 'Especial' },
  { value: 'fallback', label: 'Fallback' },
];

function mapMediaItem(item) {
  return {
    id: item.id,
    title: item.metadata?.title || item.original_filename,
    artist: item.metadata?.artist || '',
    category: item.category,
    status: item.processing_status,
    duration_seconds: item.duration_seconds,
    original_filename: item.original_filename,
    stored_filename: item.stored_filename,
    metadata: item.metadata,
    created_at: item.created_at,
    is_corrupt: item.is_corrupt,
    is_enabled: item.is_enabled !== false,
    original_size: item.original_size,
    file_size: item.processed_size || item.original_size,
    processed_bitrate: item.processed_bitrate,
    last_played_at: item.last_played_at || null,
    play_count: parseInt(item.play_count, 10) || 0,
  };
}

export default function MediaLibrary() {
  const navigate = useNavigate();
  const [media, setMedia] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [folderFilter, setFolderFilter] = useState('');
  const [search, setSearch] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null);
  const [counts, setCounts] = useState({ pending: 0, processing: 0 });
  const [queuedId, setQueuedId] = useState(null);
  const [convertingId, setConvertingId] = useState(null);

  const fetchCounts = async () => {
    try {
      const [p, pr] = await Promise.all([
        api.get('/api/media', { params: { processing_status: 'pending', limit: 1 } }),
        api.get('/api/media', { params: { processing_status: 'processing', limit: 1 } }),
      ]);
      setCounts({ pending: p.data?.total || 0, processing: pr.data?.total || 0 });
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    fetchFolders();
    fetchMedia();
    fetchCounts();
  }, [categoryFilter, statusFilter, folderFilter, search]);

  const hasActivity = media.some((m) => ['pending', 'processing', 'uploading'].includes(m.status))
    || counts.processing > 0 || counts.pending > 0;

  useEffect(() => {
    if (!hasActivity) return undefined;
    const id = setInterval(() => {
      fetchMedia(true);
      fetchCounts();
    }, 4000);
    return () => clearInterval(id);
  }, [hasActivity, categoryFilter, statusFilter, folderFilter, search]);

  const fetchFolders = async () => {
    try {
      const { data } = await api.get('/api/music/folders');
      setFolders(data);
    } catch (err) {
      console.error('[MediaLibrary] Failed to fetch folders:', err);
    }
  };

  const fetchMedia = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = {};
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (statusFilter !== 'all') params.processing_status = statusFilter;
      if (folderFilter) params.folder_id = folderFilter;
      if (search) params.search = search;

      const { data } = await api.get('/api/media', { params });
      const files = data.files || [];
      setMedia(files.map(mapMediaItem));
    } catch (err) {
      console.error('[MediaLibrary] Failed to fetch media:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const enqueueTrack = async (item) => {
    try {
      await api.post('/api/playback/enqueue', { media_file_id: item.id });
      setQueuedId(item.id);
      setTimeout(() => setQueuedId((cur) => (cur === item.id ? null : cur)), 2000);
    } catch (err) {
      console.error('[MediaLibrary] Enqueue failed:', err);
      alert('No se pudo agregar a la cola de reproducción.');
    }
  };

  const convertTo128 = async (item) => {
    try {
      await api.post(`/api/media/${item.id}/convert-128k`, {});
      setConvertingId(item.id);
    } catch (err) {
      console.error('[MediaLibrary] Convert to 128k failed:', err);
      alert('No se pudo encolar la conversión a 128k.');
    }
  };

  const handleDelete = async (item) => {
    try {
      await api.delete(`/api/media/${item.id}`);
      setMedia(media.filter((m) => m.id !== item.id));
    } catch (err) {
      console.error('[MediaLibrary] Delete failed:', err);
    }
  };

  const toggleEnabled = async (item) => {
    try {
      await api.post(`/api/media/${item.id}/toggle`, {});
      setMedia(media.map((m) => (m.id === item.id ? { ...m, is_enabled: !m.is_enabled } : m)));
    } catch (err) {
      console.error('[MediaLibrary] Toggle failed:', err);
      alert('No se pudo cambiar el estado del archivo.');
    }
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    const params = {};
    if (val) params.search = val;
    const timer = setTimeout(() => fetchMedia(), 300);
    return () => clearTimeout(timer);
  };

  const MediaGrid = () => (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {media.map((item) => (
        <div
          key={item.id}
          draggable={item.status === 'ready' && !!item.stored_filename}
          onDragStart={(e) => {
            e.dataTransfer.setData('text/plain', item.id);
            e.dataTransfer.effectAllowed = 'copy';
          }}
          className={`rounded-xl bg-dark-850 border border-dark-700 p-4 transition-transform hover:scale-[1.02] ${
            item.status === 'ready' && item.stored_filename ? 'cursor-grab active:cursor-grabbing' : ''
          }`}
          title={item.status === 'ready' ? 'Arrastralo al panel "Próximos" para agregarlo a la cola' : undefined}
        >
          <div className="relative flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg bg-dark-800 text-2xl mx-auto">
            🎵
            {item.status === 'ready' && item.stored_filename && (
              <Link
                to={`/media/${item.id}`}
                className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-white hover:bg-primary-700 text-xs"
                title="Escuchar"
              >
                ▶
              </Link>
            )}
          </div>
          <div className="mt-3 text-center">
            <Link
              to={`/media/${item.id}`}
              className="font-medium text-dark-100 hover:text-primary-400 line-clamp-1"
              title={item.title}
            >
              {item.title}
            </Link>
            {item.artist && (
              <p className="text-xs text-dark-500 line-clamp-1">{item.artist}</p>
            )}
          </div>
          <div className="mt-3 flex items-center justify-center gap-2">
            <StatusBadge status={item.status} size="sm" />
            {item.is_corrupt && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-error-900/40 text-error-400 font-semibold" title="Archivo corrupto, separado de la rotación">⚠ Corrupto</span>
            )}
            {item.category && (
              <span className="text-xs text-dark-500">[{item.category}]</span>
            )}
          </div>
          <div className="mt-2 text-center">
            <span className="text-xs text-dark-500" title={item.original_size ? `Original: ${formatFileSize(item.original_size)}` : ''}>{formatFileSize(item.file_size)}</span>
            <span className="mx-1 text-dark-700">•</span>
            <DurationDisplay seconds={item.duration_seconds} />
          </div>
          <div className="mt-1 text-center text-[11px]">
            {item.play_count > 0 ? (
              <span
                className="text-success-400"
                title={item.last_played_at ? `Última reproducción: ${format(parseISO(item.last_played_at), 'PPp')}` : 'Reproducido'}
              >
                ▶ Reproducido {item.play_count}×
              </span>
            ) : (
              <span className="text-dark-600">○ Nunca reproducido</span>
            )}
          </div>
          {item.status === 'ready' && item.stored_filename && (
            <div className="mt-3">
              <Link
                to={`/media/${item.id}`}
                className="btn btn-primary btn-sm w-full"
              >
                ▶ Escuchar
              </Link>
              <button
                type="button"
                onClick={() => enqueueTrack(item)}
                className="btn btn-secondary btn-sm w-full mt-2"
                title="Agregar a la cola de Próximos"
              >
                {queuedId === item.id ? '✓ En cola' : '➕ A cola (Próximos)'}
              </button>
              {item.status === 'ready' && item.processed_bitrate !== 128 && (
                <button
                  type="button"
                  onClick={() => convertTo128(item)}
                  className="btn btn-outline btn-sm w-full mt-2"
                  title="Convertir este archivo a 128 kbps (ahorra espacio en disco)"
                >
                  {convertingId === item.id ? '⏳ Convirtiendo…' : '↘ Convertir a 128k'}
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const MediaList = () => (
    <div className="overflow-x-auto">
      <table className="table">
        <thead>
          <tr>
            <th>Título</th>
            <th>Artista</th>
            <th>Categoría</th>
            <th>Duración</th>
            <th>Tamaño</th>
            <th>Estado</th>
            <th>Reproducción</th>
            <th className="text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {media.map((item) => (
            <tr
              key={item.id}
              draggable={item.status === 'ready' && !!item.stored_filename}
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', item.id);
                e.dataTransfer.effectAllowed = 'copy';
              }}
              className={`border-b border-dark-800 hover:bg-dark-800/40 ${
                item.status === 'ready' && item.stored_filename ? 'cursor-grab active:cursor-grabbing' : ''
              }`}
              title={item.status === 'ready' ? 'Arrastralo al panel "Próximos" para agregarlo a la cola' : undefined}
            >
              <td className="font-medium text-dark-100">
                <Link to={`/media/${item.id}`} className="hover:text-primary-400">
                  {item.title}
                </Link>
              </td>
              <td className="text-dark-300">{item.artist || '—'}</td>
              <td>
                {item.category && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-dark-700 text-dark-300">
                    {item.category}
                  </span>
                )}
                {item.is_corrupt && (
                  <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-error-900/40 text-error-400 font-semibold" title="Archivo corrupto, separado de la rotación">⚠ Corrupto</span>
                )}
              </td>
              <td>{item.duration_seconds && <DurationDisplay seconds={item.duration_seconds} />}</td>
              <td className="text-sm text-dark-400">{formatFileSize(item.file_size)}</td>
              <td>
                <StatusBadge status={item.status} size="sm" />
                <button
                  type="button"
                  onClick={() => toggleEnabled(item)}
                  className={`ml-2 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                    item.is_enabled
                      ? 'bg-success-900/30 text-success-400 hover:bg-success-900/50'
                      : 'bg-dark-700 text-dark-500 hover:bg-dark-600'
                  }`}
                  title={item.is_enabled ? 'Prendido — apagar (no se reproduce)' : 'Apagado — prender'}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${item.is_enabled ? 'bg-success-400' : 'bg-dark-500'}`}></span>
                  {item.is_enabled ? 'Prendido' : 'Apagado'}
                </button>
              </td>
              <td className="text-xs text-dark-300">
                {item.play_count > 0 ? (
                  <span title={item.last_played_at ? `Última reproducción: ${format(parseISO(item.last_played_at), 'PPp')}` : 'Reproducido'}>
                    ▶ {item.play_count}×
                  </span>
                ) : (
                  <span className="text-dark-600">○ Nunca</span>
                )}
              </td>
              <td className="text-right">
                {item.is_corrupt && (
                  <button
                    onClick={async () => {
                      try {
                        await api.post(`/api/media/${item.id}/restore`, {});
                        fetchMedia();
                      } catch (e) {
                        alert('No se pudo restaurar el archivo.');
                      }
                    }}
                    className="text-xs text-success-400 hover:text-success-300 mr-2"
                    title="Quitar marca de corrupto (vuelve a la rotación para sobreescribirlo)"
                  >
                    ↩ Restaurar
                  </button>
                )}
                {item.status === 'ready' && item.stored_filename && (
                  <Link
                    to={`/media/${item.id}`}
                    className="text-xs text-primary-400 hover:text-primary-300 mr-2"
                    title="Escuchar"
                  >
                    ▶ Escuchar
                  </Link>
                )}
                {item.status === 'ready' && item.stored_filename && (
                  <button
                    type="button"
                    onClick={() => enqueueTrack(item)}
                    className="text-xs text-success-400 hover:text-success-300 mr-2"
                    title="Agregar a la cola de Próximos"
                  >
                    {queuedId === item.id ? '✓ En cola' : '➕ A cola'}
                  </button>
                )}
                {item.status === 'ready' && item.processed_bitrate !== 128 && (
                  <button
                    type="button"
                    onClick={() => convertTo128(item)}
                    className="text-xs text-warning-400 hover:text-warning-300 mr-2"
                    title="Convertir a 128 kbps (ahorra espacio en disco)"
                  >
                    {convertingId === item.id ? '⏳ 128k…' : '↘ 128k'}
                  </button>
                )}
                <Link
                  to={`/media/${item.id}`}
                  className="text-xs text-dark-400 hover:text-dark-200 mr-1"
                  title="Ver"
                >
                  👁
                </Link>
                <button
                  onClick={() => {
                    setConfirmItem(item);
                    setConfirmOpen(true);
                  }}
                  className="text-xs text-error-400 hover:text-error-300"
                  title="Eliminar"
                >
                  ✕
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold text-dark-100">Biblioteca de Medios</h1>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={async () => {
              if (!confirm('¿Poner en pendientes todos los archivos que están en cola? Se procesarán cuando lo indiques.')) return;
              try {
                const res = await api.post('/api/media/pause-processing', {});
                alert(`${res.data?.deferred || 0} archivo(s) movidos a pendientes.`);
                fetchCounts();
                fetchMedia();
              } catch (e) {
                alert('Error al poner en pendientes.');
              }
            }}
            className="btn btn-secondary btn-sm"
            title="Mover los archivos en cola a 'pendiente' (procesar después)"
          >
            ⏸ Poner pendientes ({counts.processing})
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                const res = await api.post('/api/media/process-pending', {});
                alert(`Se encolaron ${res.data?.queued || 0} archivos pendientes.`);
                fetchCounts();
                fetchMedia();
              } catch (e) {
                alert('Error al encolar pendientes.');
              }
            }}
            className="btn btn-secondary btn-sm"
            title="Procesar todos los archivos pendientes"
          >
            ⚙️ Procesar pendientes ({counts.pending})
          </button>
          <Link to="/media/upload" className="btn btn-primary btn-sm">
            Subir Medio
          </Link>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Buscar medios..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input w-full text-sm sm:w-auto sm:max-w-sm"
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="input w-full text-sm sm:w-40"
        >
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input w-full text-sm sm:w-40"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <select
          value={folderFilter}
          onChange={(e) => setFolderFilter(e.target.value)}
          className="input w-full text-sm sm:w-48"
        >
          <option value="">Todas las Carpetas</option>
          {folders.map((f) => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
        </select>

        <div className="ml-auto flex items-center gap-2 text-sm">
          <button
            onClick={() => setViewMode('grid')}
            className={`rounded-md px-2.5 py-1.5 ${
              viewMode === 'grid' ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
            }`}
            title="Vista cuadrícula"
          >
            ⊡
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`rounded-md px-2.5 py-1.5 ${
              viewMode === 'list' ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
            }`}
            title="Vista lista"
          >
            ☰
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-dark-400">Cargando medios...</div>
      ) : media.length === 0 ? (
        <div className="py-12 text-center text-dark-400">
          No se encontraron medios. Intenta ajustar los filtros o{' '}
          <Link to="/media/upload" className="text-primary-400 hover:text-primary-300">
            sube algunos archivos
          </Link>.
        </div>
      ) : (
        viewMode === 'grid' ? <MediaGrid /> : <MediaList />
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Eliminar Medio"
        message={`¿Seguro que quieres eliminar "${confirmItem?.title}"? Esta acción no se puede deshacer.`}
        danger
        onConfirm={() => confirmItem && handleDelete(confirmItem)}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}
